import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Layout from '@components/layout';

import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import GridCard from '@repo/ui-components/GridCard';
import GridCardContainer from '@repo/ui-components/GridCardContainer';
import Hero from '@repo/ui-components/hero/Hero';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';

import getInternalLink from '@components/utils/getInternalLink';
import GearTagLine from '@components/GearTagLine';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';

function ProstaffCategory({ data, pageContext }) {
  const { SEOMetadata, title, description, image, body } =
    data?.sanityPersonCategory ?? {};
  const { linkHreflangValues } = pageContext;

  const people = data.people.nodes;

  const { tt, locale } = useSportdogInternationalContext();

  const breadcrumbs = [
    { text: tt(s => s.breadcrumbsCommon.home), link: '/' },
    { text: title },
  ];

  return (
    <Layout
      headTagData={{
        title: SEOMetadata?.title || title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description,
            url:
              SEOMetadata?.ogUrl ||
              SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              getLocalizedPagePath('/', locale),
            image: SEOMetadata?.ogImage || image || '',
          },
          'sportdog'
        ),
      }}
      linkHreflangValues={linkHreflangValues}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      {image?.asset?.url && (
        <Hero
          hero={{
            mediaItem: image.asset.url,
            heroType: 'image',
            headline1: title,
            headline2: description,
            altText:
              image?.alt || tt(s => s.prostaffCategoryPage.sportdogProStaffAlt),
            textAlignment: 'middleRight',
          }}
          getInternalLink={getInternalLink}
        />
      )}

      <PageWrapper>
        <h2>{title}</h2>

        {body && <BlockBuilder blocks={body} />}

        {people?.length > 0 && (
          <GridCardContainer>
            {people.map(person => (
              <GridCard
                key={person.title}
                item={{
                  ...person,
                  link: `/about-us/pro-staff/${person.slug.current}`,
                }}
              />
            ))}
          </GridCardContainer>
        )}
      </PageWrapper>
      <GearTagLine />
    </Layout>
  );
}

export const query = graphql`
  query SanityProStaffPageCategoryQuery(
    $_id: String
    $handle: String
    $locale: String
  ) {
    sanityPersonCategory(_id: { eq: $_id }) {
      slug {
        current
      }
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      title
      description
      people {
        title
        language
        slug {
          current
        }
        image: _rawImage(resolveReferences: { maxDepth: 3 })
        description
      }
      body: _rawLocalizedBody(resolveReferences: { maxDepth: 5 })
    }

    people: allSanityPerson(
      filter: {
        personCategory: { slug: { current: { eq: $handle } } }
        siteIdentifier: { eq: "sportdog" }
        language: { eq: $locale }
      }
    ) {
      nodes {
        title
        slug {
          current
        }
        image: _rawImage(resolveReferences: { maxDepth: 3 })
        description
      }
    }
  }
`;

ProstaffCategory.propTypes = {
  data: PropTypes.shape({
    sanityPersonCategory: PropTypes.shape({
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
        alt: PropTypes.string,
      }),
      body: PropTypes.arrayOf(PropTypes.shape({})),
      breadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
    }),

    people: PropTypes.shape({ nodes: PropTypes.arrayOf(PropTypes.shape({})) }),
  }).isRequired,
  pageContext: PropTypes.shape({
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default ProstaffCategory;
